import {
	KeyboardAvoidingView,
	Modal,
	StyleSheet,
	Text,
	View,
	type GestureResponderEvent,
	type NativeSyntheticEvent,
	type NativeTouchEvent,
	type TextInputKeyPressEvent,
} from 'react-native';
import { HelperText, TextInput } from 'react-native-paper';
import Translations from 'src/core/utils/Translations';
import { PrimaryButton, SecondaryButton } from 'src/presentation/components/molecules/Button';
import dialogStyles from 'src/presentation/dialogs/styles/DialogStyles';
import {
	ReservationDate,
	ReservationID,
	ReservationLocation,
	ReservationPersons,
	ReservationTime,
} from 'src/presentation/components/molecules/ReservationAttributes';
import Colors from 'src/core/utils/Colors';
import Dimens from 'src/core/utils/Dimens';

export interface Props {
	arrivalDate: string;
	arrivalTime: string;
	buttonText?: string;
	buttonTextClaim?: string;
	departureDate: string;
	departureTime: string;
	emailText: string;
	isLoading?: boolean;
	messageError?: string;
	messageText: string;
	nameText: string;
	onKeyPress?: (e: TextInputKeyPressEvent) => void;
	onPress?: (event: GestureResponderEvent) => void;
	onPressClaim?: (event: GestureResponderEvent) => void;
	onPressIn?: (e: NativeSyntheticEvent<NativeTouchEvent>) => void;
	personsCount: number;
	reservationId: string | number;
	setMessageText: (text: string) => void;
	text?: string;
	title?: string | number;
	visible?: boolean;
	workspaceTitle: string;
}

const ReactToReservationDialog = ({
	arrivalDate,
	arrivalTime,
	buttonText = Translations.cancel,
	buttonTextClaim = Translations.send,
	departureDate,
	departureTime,
	emailText,
	isLoading = false,
	messageError,
	messageText,
	nameText,
	onKeyPress,
	onPress,
	onPressClaim,
	onPressIn,
	personsCount,
	reservationId,
	setMessageText,
	title,
	visible,
	workspaceTitle,
}: Props) => {
	return (
		<Modal animationType="fade" style={dialogStyles.modalView} transparent={true} visible={visible}>
			<KeyboardAvoidingView behavior="padding" style={dialogStyles.centeredView}>
				<View style={dialogStyles.modalCard}>
					<Text style={dialogStyles.modalTitle}>{title}</Text>

					<View style={styles.divider} />

					<View style={styles.attributesRow}>
						<View style={styles.buttonColumn}>
							<ReservationDate arrivalDate={arrivalDate} departureDate={departureDate} />
							<ReservationPersons personsCount={personsCount} />
						</View>
						<View style={styles.buttonColumn}>
							<ReservationTime arrivalTime={arrivalTime} departureTime={departureTime} />
							<ReservationLocation location={workspaceTitle} />
						</View>
					</View>

					<Text>
						<ReservationID id={reservationId} />
					</Text>

					<View style={styles.divider} />

					<Text style={styles.sendAsBanner}>
						{Translations.sendAs}
						&nbsp;
						<Text style={{ fontWeight: Dimens.fontWeight700 }}>
							{nameText} &lt;
							{emailText}
							&gt;
						</Text>
					</Text>

					<TextInput
						activeUnderlineColor={Colors.green}
						label={Translations.message}
						multiline={true}
						numberOfLines={3}
						selectionColor={Colors.gray600}
						style={styles.messageInput}
						value={messageText}
						onChangeText={setMessageText}
						onKeyPress={onKeyPress}
						onPressIn={onPressIn}
					/>

					<HelperText style={{ marginBottom: Dimens.padding20 }} type="error" visible={!!messageError}>
						{messageError}
					</HelperText>

					<View style={{ flexDirection: 'row' }}>
						<SecondaryButton
							extraStyle={{ paddingHorizontal: Dimens.padding10, flex: Dimens.flex1 }}
							label={buttonText}
							onPress={onPress}
						/>
						<PrimaryButton
							extraStyle={{ paddingHorizontal: Dimens.padding10, flex: Dimens.flex1 }}
							label={buttonTextClaim}
							loading={isLoading}
							onPress={onPressClaim}
						/>
					</View>
				</View>
			</KeyboardAvoidingView>
		</Modal>
	);
};

const styles = StyleSheet.create({
	buttonColumn: {
		gap: Dimens.gap10,
		width: '50%',
	},
	divider: {
		marginBottom: Dimens.padding20,
		borderBottomWidth: Dimens.borderWidth1,
		borderBottomColor: Colors.gray200,
		paddingBottom: Dimens.padding20,
		marginHorizontal: Dimens.paddingMinus20,
	},
	attributesRow: {
		gap: Dimens.gap10,
		flexDirection: 'row',
		marginBottom: Dimens.padding10,
	},
	sendAsBanner: {
		backgroundColor: Colors.gray200,
		paddingHorizontal: Dimens.padding14,
		paddingVertical: Dimens.padding10,
		marginBottom: Dimens.padding10,
		borderRadius: Dimens.borderRadius7,
	},
	messageInput: {
		backgroundColor: Colors.gray200,
		minHeight: Dimens.size120,
	},
});

export default ReactToReservationDialog;
